import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { CustomCheckbox, RadioButton } from 'ui';

export interface AdvisorAccessFormik {
  is_not_authorized_representative: boolean;
  is_not_copies: boolean;
  is_only_authorized_representative: boolean;
  is_financial_adviser_operate: boolean;
}

const radioButtonItems = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

export const validationSchemaAdvisorAccess = yup.object({
  is_not_authorized_representative: yup.boolean(),
  is_not_copies: yup.boolean(),
  is_only_authorized_representative: yup.boolean(),
  is_financial_adviser_operate: yup
    .boolean()
    .equals([true], 'You must have permission to sign up this account as an advisor.')
    .required('This field is required'),
});

export const initialValuesAdvisorAccess: AdvisorAccessFormik = {
  is_not_authorized_representative: false,
  is_not_copies: false,
  is_only_authorized_representative: false,
  is_financial_adviser_operate: true,
};

export const AdvisorAccess = ({
  moveForward,
  entity_type_prefix,
  allowSkip,
}: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const formik = useFormik<AdvisorAccessFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaAdvisorAccess,
    initialValues: {
      ...initialValuesAdvisorAccess,
      ...(data?.entity?.[entity_type_prefix] as AdvisorAccessFormik),
    },
    onSubmit: async values => {
      if (formik.dirty) {
        await updateEntity({
          ...data?.entity,
          [entity_type_prefix]: {
            ...data?.entity[entity_type_prefix as keyof typeof data.entity],
            ...values,
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeCheckbox = (field: string, checked: boolean) => {
    formik.setFieldValue(field, checked);
  };

  const handleChangeRadio = (value: boolean) => {
    formik.setFieldValue('is_financial_adviser_operate', value);
    formik.setFieldError('is_financial_adviser_operate', '');
  };
  return (
    <OnboardingWrapper
      title="Access to information"
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col space-y-8">
        <div className="w-full flex flex-col space-y-4">
          <p className="text-[14px] leading-[22px]">
            Unless you elect otherwise, your authorised representative, agent and/or financial
            adviser will also be provided access to your investment information and/or receive
            copies of statements and transaction confirmations. By appointing an authorised
            representative, agent and/or financial adviser you acknowledge that you have read and
            agreed to the terms and conditions in the IM relating to such appointment.
          </p>
          <CustomCheckbox
            label="Please tick this box if you DO NOT want your authorised representative, agent and/or financial adviser to have access to information about your investment. (optional)"
            checked={formik.values.is_not_authorized_representative}
            onChange={e =>
              handleChangeCheckbox('is_not_authorized_representative', e.target.checked)
            }
          />
          <CustomCheckbox
            label="Please tick this box if you DO NOT want copies of statements and transaction confirmations sent to your authorised representative, agent and/or financial adviser. (optional)"
            checked={formik.values.is_not_copies}
            onChange={e => handleChangeCheckbox('is_not_copies', e.target.checked)}
          />
          <CustomCheckbox
            label="Please tick this box if you want statements and transaction confirmations sent ONLY to your authorised representative, agent and/or financial adviser. (optional)"
            checked={formik.values.is_only_authorized_representative}
            onChange={e =>
              handleChangeCheckbox('is_only_authorized_representative', e.target.checked)
            }
          />
        </div>
        <div className="w-full flex flex-col">
          <p className="text-[14px] leading-[22px] pb-4">
            Do you have permission from this investor to operate their account?
          </p>
          <RadioButton
            items={radioButtonItems}
            value={formik.values.is_financial_adviser_operate}
            onChange={value => handleChangeRadio(value)}
          />
          {formik.errors.is_financial_adviser_operate && (
            <p className="mt-1 text-sm text-[12px] text-error-red">
              {formik.errors.is_financial_adviser_operate}
            </p>
          )}
        </div>
      </div>
    </OnboardingWrapper>
  );
};
